import React, { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { useLocation } from "react-router-dom";

const API_BASE = "https://dolphin-app-33jp4.ondigitalocean.app";
const APP_ID   = "athenastar";
const isTV     = () => Capacitor.isNativePlatform();

const api = (path, body) =>
  fetch(`${API_BASE}${path}`, {
    method: body ? "POST" : "GET",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json());

// ─── collage images for left panel ───────────────────────────────
const COLLAGE = [
  "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1594908900066-3f47337549d8?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1509347528160-9a9e33742cdb?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=500&fit=crop",
  "https://images.unsplash.com/photo-1535016120720-40c646be5580?w=400&h=500&fit=crop",
];

export default function Login({ onLogin }) {
  const location    = useLocation();
  const initialMode = new URLSearchParams(location.search).get("mode") || "login";
  return isTV()
    ? <TVLogin  onLogin={onLogin} initialMode={initialMode} />
    : <WebLogin onLogin={onLogin} initialMode={initialMode} />;
}

// ════════════════════════════════════════════════════════════════════
// TV LOGIN  — Netflix full-screen layout (collage left, QR+form right)
// ════════════════════════════════════════════════════════════════════
function TVLogin({ onLogin, initialMode }) {
  const fs = 0.85; // TV font-scale factor — tuned for 1920x1080

  // QR state
  const [qrUrl,    setQrUrl]    = useState(null);
  const [token,    setToken]    = useState(null);
  const [qrSt,     setQrSt]    = useState("loading"); // loading | ready | error | done
  // form state
  const [tab,      setTab]      = useState("qr");     // qr | manual
  const [mmode,    setMmode]    = useState(initialMode === "signup" ? "signup" : "login");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [otp,      setOtp]      = useState(["","","","","",""]);
  const [name,     setName]     = useState("");
  const [newPw,    setNewPw]    = useState("");
  const [isNew,    setIsNew]    = useState(false);
  const [errMsg,   setErrMsg]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);
  const pollRef = useRef(null);
  const otpRefs = useRef([]);

  useEffect(() => { genQR(); return () => clearInterval(pollRef.current); }, []);

  // ── Force fullscreen on setup box browsers ──
  useEffect(() => {
    const el = document.documentElement;
    el.style.cssText += ';height:100%!important;overflow:hidden!important;';
    document.body.style.cssText += ';height:100%!important;overflow:hidden!important;margin:0!important;padding:0!important;';
    return () => {};
  }, []);

  async function genQR() {
    setQrSt("loading");
    try {
      const d = await api(`/auth/generate_tv_session?app_id=${APP_ID}`);
      setQrUrl(d.qr_url); setToken(d.token); setQrSt("ready");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api(`/auth/check_tv_login/${d.token}?app_id=${APP_ID}`);
          if (r.status === "logged_in") {
            clearInterval(pollRef.current); setQrSt("done"); setSuccess(true);
            setTimeout(() => onLogin?.(r.user), 1200);
          } else if (r.status === "expired") {
            clearInterval(pollRef.current); genQR();
          }
        } catch {}
      }, 3000);
    } catch { setQrSt("error"); }
  }

  // D-pad navigation
  useEffect(() => {
    const h = (e) => {
      if (tab === "qr"     && (e.key === "ArrowRight" || e.key === "Enter")) { setTab("manual"); setMmode("login"); }
      if (tab === "manual" && e.key === "Escape") { setTab("qr"); setMmode("login"); setErrMsg(""); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [tab]);

  // ── API helpers ──
  async function doLogin() {
    if (!email || !password) return setErrMsg("Please fill all fields");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/login_with_password", { email, password, app_id: APP_ID, tv_token: token });
    setLoading(false);
    if (d.user) { setSuccess(true); setTimeout(() => onLogin?.(d.user), 1200); }
    else setErrMsg(d.detail || "Login failed");
  }
  async function doSendOtp() {
    if (!email || !email.includes("@")) return setErrMsg("Enter a valid email");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/signup_send_otp", { email, app_id: APP_ID });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail);
    setIsNew(d.is_new); setMmode("otp");
  }
  async function doVerifyOtp() {
    const code = otp.join("");
    if (code.length !== 6) return setErrMsg("Enter all 6 digits");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/verify_otp", { email, otp: code, app_id: APP_ID, tv_token: token });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail);
    if (isNew) setMmode("name");
    else { setSuccess(true); setTimeout(() => onLogin?.(d.user), 1200); }
  }
  async function doSaveName() {
    if (!name) return setErrMsg("Please enter your name");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/update_name", { email, name, app_id: APP_ID });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail);
    setMmode("setpw");
  }
  async function doSetPw() {
    if (!newPw || newPw.length < 6) return setErrMsg("Minimum 6 characters");
    setLoading(true); setErrMsg("");
    await api("/auth/set_password", { email, password: newPw, app_id: APP_ID });
    const ud = await api("/auth/update_name", { email, name, app_id: APP_ID });
    setLoading(false);
    setSuccess(true); setTimeout(() => onLogin?.(ud.user), 1200);
  }
  function handleOtpKey(val, idx) {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[idx] = v; setOtp(next);
    if (v && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (next.join("").length === 6) setTimeout(doVerifyOtp, 100);
  }

  const mmTitles = { login:"Sign in", signup:"Create account", otp:"Verify email", name:"Your name", setpw:"Set password" };
  const mmSubs   = { login:"Enter your credentials", signup:"Enter your email to get started", otp:`Code sent to ${email}`, name:"What should we call you?", setpw:"Create a secure password" };

  // ─── success screen ───
  if (success) return (
    <div style={{ position:"fixed", top:0, left:0, width:"100vw", height:"100vh", background:"#060d1a", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Segoe UI',sans-serif" }}>
      <style>{`html,body,#root{margin:0!important;padding:0!important;width:100%!important;height:100%!important;background:#060d1a!important;}`}</style>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:90*fs, height:90*fs, borderRadius:"50%", background:"rgba(20,184,166,.12)", border:"2px solid #14b8a6", display:"flex", alignItems:"center", justifyContent:"center", margin:`0 auto ${28*fs}px` }}>
          <svg width={40*fs} height={40*fs} viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h1 style={{ color:"#f1f5f9", fontSize:44*fs, fontWeight:700, letterSpacing:"-1px", margin:"0 0 10px" }}>Welcome!</h1>
        <p style={{ color:"#64748b", fontSize:18*fs }}>Signing you in…</p>
      </div>
    </div>
  );

  return (
    <div style={{ position:"fixed", top:0, left:0, width:"100vw", height:"100vh", display:"flex", fontFamily:"'Segoe UI',sans-serif", background:"#060d1a", overflow:"hidden" }}>
      <style>{`
        html, body, #root { margin:0 !important; padding:0 !important; width:100% !important; height:100% !important; overflow:hidden !important; background:#060d1a !important; }
        @keyframes spin  { to { transform:rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:.25} }
        input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;}
        input[type=number]{-moz-appearance:textfield;}
        input:focus{outline:none!important;border-color:#14b8a6!important;box-shadow:0 0 0 3px rgba(20,184,166,.18)!important;}
        .tvbtn:hover{opacity:.85;} .tvbtn:active{transform:scale(.97);}
        *{box-sizing:border-box;}
      `}</style>

      {/* ── LEFT collage ── */}
      <div style={{ flex:"0 0 44%", position:"relative", overflow:"hidden", height:"100vh" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gridTemplateRows:"repeat(4,1fr)", gap:4, padding:4, width:"100%", height:"100%" }}>
          {COLLAGE.map((src,i) => <img key={i} src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:6 }} />)}
        </div>
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to right,rgba(6,13,26,0.05) 0%,rgba(6,13,26,0.5) 70%,rgba(6,13,26,1) 100%)" }} />
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(6,13,26,.8) 0%,transparent 40%)" }} />
        {/* Brand */}
        <div style={{ position:"absolute", bottom:52, left:44 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:10 }}>
            <div style={{ width:52*fs, height:52*fs, background:"linear-gradient(135deg,#14b8a6,#0891b2)", borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(20,184,166,.5)", flexShrink:0 }}>
              <span style={{ color:"#fff", fontWeight:800, fontSize:24*fs }}>A</span>
            </div>
            <span style={{ color:"#fff", fontWeight:800, fontSize:36*fs, letterSpacing:"-0.5px" }}>AthenaStar</span>
          </div>
          <p style={{ color:"rgba(255,255,255,0.35)", fontSize:14*fs, margin:0 }}>Your world of content. All in one place.</p>
        </div>
      </div>

      {/* ── RIGHT ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", padding:`20px ${40*fs}px`, overflow:"hidden", height:"100vh" }}>
        <p style={{ color:"#1e3a5f", fontSize:13*fs, margin:`0 0 ${12*fs}px`, textAlign:"right" }}>
          {tab === "qr" ? "Press → or Enter for manual login" : "Press Esc to return to QR scan"}
        </p>

        <div style={{ flex:1, display:"flex", gap:40, alignItems:"stretch" }}>

          {/* QR panel */}
          <div onClick={() => setTab("qr")}
            style={{ flex:1, borderRadius:20, padding:`${20*fs}px ${22*fs}px`, border:"1px solid transparent", cursor:"pointer", transition:"all .2s", display:"flex", flexDirection:"column", justifyContent:"center",
              ...(tab === "qr"
                ? { background:"#0a1929", borderColor:"#14b8a6", boxShadow:"0 0 0 1px rgba(20,184,166,.2),0 20px 60px rgba(0,0,0,.5)" }
                : { background:"#060f1c", borderColor:"#0f2035", opacity:.5 }) }}>
            <p style={{ color:"#f1f5f9", fontSize:22*fs, fontWeight:700, margin:`0 0 ${3*fs}px` }}>Scan to sign in</p>
            <p style={{ color:"#475569", fontSize:13*fs, margin:`0 0 ${14*fs}px` }}>Open your phone camera and scan</p>
            <div style={{ width:150*fs, height:150*fs, background:"#fff", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", margin:`0 auto ${14*fs}px`, overflow:"hidden" }}>
              {qrSt === "loading" && <div style={{ width:36, height:36, border:"3px solid #e2e8f0", borderTopColor:"#14b8a6", borderRadius:"50%", animation:"spin .8s linear infinite" }}/>}
              {qrSt === "error"   && <button className="tvbtn" onClick={e=>{e.stopPropagation();genQR();}} style={{ padding:"10px 22px", background:"#14b8a6", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontSize:14*fs, fontWeight:700 }}>Retry</button>}
              {qrSt === "ready"   && <img src={qrUrl} alt="QR" style={{ width:"100%", height:"100%" }}/>}
              {qrSt === "done"    && <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10*fs }}>
              {["Open phone camera","Scan QR code","Login or signup on phone"].map((s,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:22*fs, height:22*fs, borderRadius:"50%", background:"rgba(20,184,166,.15)", color:"#14b8a6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11*fs, fontWeight:700, flexShrink:0 }}>{i+1}</div>
                  <span style={{ fontSize:13*fs, color:"#475569" }}>{s}</span>
                </div>
              ))}
            </div>
            {qrSt === "ready" && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:16*fs }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:"#14b8a6", animation:"pulse 1.5s ease-in-out infinite", flexShrink:0 }}/>
                <span style={{ fontSize:12*fs, color:"#475569" }}>Waiting for authentication</span>
              </div>
            )}
          </div>

          {/* OR separator */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10, alignSelf:"stretch" }}>
            <div style={{ width:1, flex:1, background:"#0f2035" }}/><span style={{ fontSize:11*fs, color:"#1e3a5f", letterSpacing:1, textTransform:"uppercase" }}>or</span><div style={{ width:1, flex:1, background:"#0f2035" }}/>
          </div>

          {/* Manual form panel */}
          <div onClick={() => tab !== "manual" && setTab("manual")}
            style={{ flex:1, borderRadius:20, padding:`${20*fs}px ${22*fs}px`, border:"1px solid transparent", cursor: tab === "manual" ? "default" : "pointer", transition:"all .2s", display:"flex", flexDirection:"column", justifyContent:"center",
              ...(tab === "manual"
                ? { background:"#0a1929", borderColor:"#14b8a6", boxShadow:"0 0 0 1px rgba(20,184,166,.2),0 20px 60px rgba(0,0,0,.5)" }
                : { background:"#060f1c", borderColor:"#0f2035", opacity:.5 }) }}>

            <p style={{ color:"#f1f5f9", fontSize:22*fs, fontWeight:700, margin:`0 0 ${3*fs}px` }}>{mmTitles[mmode]}</p>
            <p style={{ color:"#475569", fontSize:13*fs, margin:`0 0 ${14*fs}px` }}>{mmSubs[mmode]}</p>

            <div style={{ display:"flex", flexDirection:"column", gap:10*fs }}>
              {/* LOGIN */}
              {mmode === "login" && <>
                <div><label style={TVlbl(fs)}>Email address</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={TVinp(fs)} placeholder="you@example.com" onKeyDown={e=>e.key==="Enter"&&doLogin()} /></div>
                <div>
                  <label style={TVlbl(fs)}>Password</label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} style={TVinp(fs)} placeholder="Enter password" onKeyDown={e=>e.key==="Enter"&&doLogin()} />
                </div>
                {errMsg && <p style={TVerr}>{errMsg}</p>}
                <button className="tvbtn" style={TVbtnP(fs)} onClick={doLogin} disabled={loading}>{loading?"Signing in…":"Sign in"}</button>
                <button className="tvbtn" style={TVbtnO(fs)} onClick={()=>{setErrMsg("");setMmode("signup");}}>New user? Sign up with OTP</button>
              </>}
              {/* SIGNUP */}
              {mmode === "signup" && <>
                <div><label style={TVlbl(fs)}>Email address</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={TVinp(fs)} placeholder="you@example.com" onKeyDown={e=>e.key==="Enter"&&doSendOtp()} /></div>
                {errMsg && <p style={TVerr}>{errMsg}</p>}
                <button className="tvbtn" style={TVbtnP(fs)} onClick={doSendOtp} disabled={loading}>{loading?"Sending…":"Send verification code"}</button>
                <button className="tvbtn" style={TVbtnO(fs)} onClick={()=>{setErrMsg("");setMmode("login");}}>Back to sign in</button>
              </>}
              {/* OTP */}
              {mmode === "otp" && <>
                <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                  {otp.map((v,i) => <input key={i} ref={el=>otpRefs.current[i]=el} type="number" value={v} onChange={e=>handleOtpKey(e.target.value,i)} style={{ width:54*fs, height:64*fs, textAlign:"center", fontSize:24*fs, fontWeight:700, background:"#020b18", border:"1px solid #1e3a5f", borderRadius:10, color:"#f1f5f9", outline:"none" }} />)}
                </div>
                {errMsg && <p style={TVerr}>{errMsg}</p>}
                <button className="tvbtn" style={TVbtnP(fs)} onClick={doVerifyOtp} disabled={loading}>{loading?"Verifying…":"Verify code"}</button>
                <button className="tvbtn" style={TVbtnO(fs)} onClick={doSendOtp}>Resend code</button>
              </>}
              {/* NAME */}
              {mmode === "name" && <>
                <div><label style={TVlbl(fs)}>Full name</label><input type="text" value={name} onChange={e=>setName(e.target.value)} style={TVinp(fs)} placeholder="Your name" onKeyDown={e=>e.key==="Enter"&&doSaveName()} /></div>
                {errMsg && <p style={TVerr}>{errMsg}</p>}
                <button className="tvbtn" style={TVbtnP(fs)} onClick={doSaveName} disabled={loading}>{loading?"Saving…":"Continue"}</button>
              </>}
              {/* SETPW */}
              {mmode === "setpw" && <>
                <div>
                  <label style={TVlbl(fs)}>Create password</label>
                  <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} style={TVinp(fs)} placeholder="Minimum 6 characters" onKeyDown={e=>e.key==="Enter"&&doSetPw()} />
                </div>
                {errMsg && <p style={TVerr}>{errMsg}</p>}
                <button className="tvbtn" style={TVbtnP(fs)} onClick={doSetPw} disabled={loading}>{loading?"Creating account…":"Create account"}</button>
              </>}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// WEB LOGIN — Netflix full-screen layout (collage left, form + QR right)
// ════════════════════════════════════════════════════════════════════
function WebLogin({ onLogin, initialMode }) {
  const [mode,    setMode]    = useState(initialMode === "signup" ? "signup" : "login");
  const [email,   setEmail]   = useState("");
  const [password,setPassword]= useState("");
  const [showPw,  setShowPw]  = useState(false);
  const [newPw,   setNewPw]   = useState("");
  const [name,    setName]    = useState("");
  const [otp,     setOtp]     = useState(["","","","","",""]);
  const [isNew,   setIsNew]   = useState(false);
  const [errMsg,  setErrMsg]  = useState("");
  const [loading, setLoading] = useState(false);
  const [qrUrl,   setQrUrl]   = useState(null);
  const [qrSt,    setQrSt]    = useState("loading");
  const pollRef = useRef(null);
  const otpRefs = useRef([]);

  useEffect(() => { generateQR(); return () => clearInterval(pollRef.current); }, []);

  async function generateQR() {
    setQrSt("loading");
    try {
      const d = await api(`/auth/generate_tv_session?app_id=${APP_ID}`);
      setQrUrl(d.qr_url); setQrSt("ready");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api(`/auth/check_tv_login/${d.token}?app_id=${APP_ID}`);
          if (r.status === "logged_in") { clearInterval(pollRef.current); setQrSt("done"); onLogin?.(r.user); }
          else if (r.status === "expired") { clearInterval(pollRef.current); generateQR(); }
        } catch {}
      }, 3000);
    } catch { setQrSt("error"); }
  }

  async function loginPassword() {
    if (!email || !password) return setErrMsg("Please fill in all fields");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/login_with_password", { email, password, app_id: APP_ID });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail === "NO_PASSWORD" ? "No password set. Please use OTP login." : d.detail);
    onLogin?.(d.user);
  }
  async function sendOtp() {
    if (!email || !email.includes("@")) return setErrMsg("Enter a valid email address");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/signup_send_otp", { email, app_id: APP_ID });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail);
    setIsNew(d.is_new); setMode("otp");
  }
  async function verifyOtp() {
    const code = otp.join("");
    if (code.length !== 6) return setErrMsg("Enter the complete 6-digit code");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/verify_otp", { email, otp: code, app_id: APP_ID, tv_token: null });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail);
    if (isNew) setMode("name"); else onLogin?.(d.user);
  }
  async function completeName() {
    if (!name) return setErrMsg("Please enter your name");
    setLoading(true); setErrMsg("");
    const d = await api("/auth/update_name", { email, name, app_id: APP_ID });
    setLoading(false);
    if (d.detail) return setErrMsg(d.detail);
    setMode("setpw");
  }
  async function completePassword() {
    if (!newPw || newPw.length < 6) return setErrMsg("Password must be at least 6 characters");
    setLoading(true); setErrMsg("");
    await api("/auth/set_password", { email, password: newPw, app_id: APP_ID });
    const ud = await api("/auth/update_name", { email, name, app_id: APP_ID });
    setLoading(false);
    onLogin?.(ud.user);
  }
  function handleOtpInput(val, idx) {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[idx] = v; setOtp(next);
    if (v && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (next.join("").length === 6) setTimeout(verifyOtp, 100);
  }
  function handleOtpBack(e, idx) {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  }

  const isSignupFlow = ["signup","otp","name","setpw"].includes(mode);
  const headings = {
    login:  { title:"Sign in",           sub:"Welcome back to AthenaStar" },
    signup: { title:"Create account",    sub:"Enter your email to get started" },
    otp:    { title:"Verify your email", sub:`We sent a 6-digit code to ${email}` },
    name:   { title:"Your name",         sub:"What should we call you?" },
    setpw:  { title:"Set a password",    sub:"Create a secure password (min 6 chars)" },
  };
  const H = headings[mode] || headings.login;

  return (
    <div style={{ position:"fixed", inset:0, fontFamily:"'Segoe UI','SF Pro Text',-apple-system,sans-serif", overflow:"hidden" }}>
      <style>{`
        @keyframes spin   { to { transform:rotate(360deg); } }
        @keyframes pulse  { 0%,100%{opacity:1}50%{opacity:.25} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none} }
        input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;}
        input[type=number]{-moz-appearance:textfield;}
        input:focus{outline:none!important;border-color:#14b8a6!important;box-shadow:0 0 0 3px rgba(20,184,166,.18)!important;}
        .wbtn:hover{opacity:.87;} .wbtn:active{transform:scale(.98);}
        *{box-sizing:border-box;}
      `}</style>

      {/* ── FULLSCREEN COLLAGE BG ── */}
      <div style={{ position:"absolute", inset:0 }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gridTemplateRows:"repeat(3,1fr)", gap:4, width:"100%", height:"100%" }}>
          {COLLAGE.map((src,i) => <img key={i} src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />)}
        </div>
        {/* dark overlay — heavier in center for readability */}
        <div style={{ position:"absolute", inset:0, background:"rgba(6,10,20,0.72)" }}/>
        {/* subtle vignette */}
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at center, rgba(6,10,20,0.3) 0%, rgba(6,10,20,0.75) 100%)" }}/>
      </div>

      {/* ── BRAND — bottom left ── */}
      <div style={{ position:"absolute", bottom:32, left:36, zIndex:10, animation:"fadeUp .5s ease both" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
          <div style={{ width:42, height:42, background:"linear-gradient(135deg,#14b8a6,#0891b2)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 16px rgba(20,184,166,.5)", flexShrink:0 }}>
            <span style={{ color:"#fff", fontWeight:800, fontSize:19 }}>A</span>
          </div>
          <span style={{ color:"#fff", fontWeight:800, fontSize:24, letterSpacing:"-0.4px", textShadow:"0 2px 12px rgba(0,0,0,0.5)" }}>AthenaStar</span>
        </div>
        <p style={{ color:"rgba(255,255,255,0.4)", fontSize:12, margin:0 }}>Your world of content. All in one place.</p>
      </div>

      {/* ── TOP BAR ── */}
      <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:10, display:"flex", justifyContent:"flex-end", alignItems:"center", padding:"22px 36px" }}>
        <span style={{ color:"rgba(255,255,255,0.5)", fontSize:13, marginRight:12 }}>
          {isSignupFlow ? "Already have an account?" : "New to AthenaStar?"}
        </span>
        <button className="wbtn" style={{ padding:"8px 22px", borderRadius:8, border:"1.5px solid rgba(255,255,255,0.3)", background:"rgba(255,255,255,0.08)", backdropFilter:"blur(8px)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", letterSpacing:"0.03em" }}
          onClick={() => { setErrMsg(""); setMode(isSignupFlow ? "login" : "signup"); }}>
          {isSignupFlow ? "Sign in" : "Sign up"}
        </button>
      </div>

      {/* ── CENTER — form + QR ── */}
      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", gap:24, padding:"80px 40px 60px", zIndex:5, overflowY:"auto" }}>


          {/* ── FORM CARD ── */}
          <div style={{ flexShrink:0, width:420, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.09)", borderRadius:20, padding:"36px 36px 32px", boxShadow:"0 24px 80px rgba(0,0,0,0.5)", backdropFilter:"blur(12px)", animation:"fadeUp .4s ease both" }}>

            {/* title */}
            <h1 style={{ color:"#fff", fontSize:28, fontWeight:700, letterSpacing:"-0.5px", margin:"0 0 4px" }}>{H.title}</h1>
            <p style={{ color:"rgba(255,255,255,0.35)", fontSize:13, margin:"0 0 28px", lineHeight:1.5 }}>{H.sub}</p>

            {/* LOGIN */}
            {mode === "login" && <>
              <div style={{ marginBottom:18 }}>
                <label style={Wlbl}>EMAIL ADDRESS</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={Winp} placeholder="name@example.com" onKeyDown={e=>e.key==="Enter"&&loginPassword()} />
              </div>
              <div style={{ marginBottom:20 }}>
                <label style={Wlbl}>PASSWORD</label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)} style={Winp} placeholder="Your password" onKeyDown={e=>e.key==="Enter"&&loginPassword()} />
              </div>
              {errMsg && <p style={Werr}>{errMsg}</p>}
              <button className="wbtn" style={WbtnP} onClick={loginPassword} disabled={loading}>{loading?"Signing in…":"Sign in"}</button>
              <div style={{ display:"flex", alignItems:"center", gap:12, margin:"14px 0" }}>
                <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.07)" }}/>
                <span style={{ color:"rgba(255,255,255,0.2)", fontSize:11, letterSpacing:"0.05em" }}>OR CONTINUE WITH</span>
                <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.07)" }}/>
              </div>
              <button className="wbtn" style={WbtnS} onClick={()=>{setErrMsg("");setMode("signup");}}>Continue with Email OTP</button>
              <p style={{ textAlign:"center", color:"rgba(255,255,255,0.25)", fontSize:12, marginTop:14, lineHeight:1.6 }}>
                Don't have an account?{" "}<span style={{ color:"#14b8a6", cursor:"pointer", fontWeight:600 }} onClick={()=>{setErrMsg("");setMode("signup");}}>Create one</span>
              </p>
            </>}

            {/* SIGNUP */}
            {mode === "signup" && <>
              <div style={{ marginBottom:20 }}>
                <label style={Wlbl}>EMAIL ADDRESS</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} style={Winp} placeholder="name@example.com" onKeyDown={e=>e.key==="Enter"&&sendOtp()} />
              </div>
              {errMsg && <p style={Werr}>{errMsg}</p>}
              <button className="wbtn" style={WbtnP} onClick={sendOtp} disabled={loading}>{loading?"Sending code…":"Send verification code"}</button>
              <p style={{ textAlign:"center", color:"rgba(255,255,255,0.25)", fontSize:12, marginTop:14 }}>
                Already have an account?{" "}<span style={{ color:"#14b8a6", cursor:"pointer", fontWeight:600 }} onClick={()=>{setErrMsg("");setMode("login");}}>Sign in</span>
              </p>
            </>}

            {/* OTP */}
            {mode === "otp" && <>
              <p style={{ color:"rgba(255,255,255,0.35)", fontSize:12, marginBottom:20, textAlign:"center" }}>Enter the 6-digit code sent to <strong style={{ color:"#14b8a6" }}>{email}</strong></p>
              <div style={{ display:"flex", gap:10, justifyContent:"center", marginBottom:20 }}>
                {otp.map((v,i) => (
                  <input key={i} ref={el=>otpRefs.current[i]=el} type="number" value={v}
                    onChange={e=>handleOtpInput(e.target.value,i)} onKeyDown={e=>handleOtpBack(e,i)}
                    style={{ width:52, height:62, textAlign:"center", fontSize:24, fontWeight:700, background:"rgba(255,255,255,0.07)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, color:"#f1f5f9", outline:"none" }} />
                ))}
              </div>
              {errMsg && <p style={Werr}>{errMsg}</p>}
              <button className="wbtn" style={WbtnP} onClick={verifyOtp} disabled={loading}>{loading?"Verifying…":"Verify code"}</button>
              <button className="wbtn" style={{ ...WbtnS, marginTop:0 }} onClick={sendOtp}>Resend code</button>
              <p style={{ textAlign:"center", color:"rgba(255,255,255,0.25)", fontSize:12, marginTop:12 }}>
                <span style={{ color:"#14b8a6", cursor:"pointer" }} onClick={()=>{setMode("signup");setErrMsg("");}}>← Change email</span>
              </p>
            </>}

            {/* NAME */}
            {mode === "name" && <>
              <div style={{ marginBottom:20 }}>
                <label style={Wlbl}>FULL NAME</label>
                <input type="text" value={name} onChange={e=>setName(e.target.value)} style={Winp} placeholder="Your full name" onKeyDown={e=>e.key==="Enter"&&completeName()} />
              </div>
              {errMsg && <p style={Werr}>{errMsg}</p>}
              <button className="wbtn" style={WbtnP} onClick={completeName} disabled={loading}>{loading?"Saving…":"Continue"}</button>
            </>}

            {/* SETPW */}
            {mode === "setpw" && <>
              <div style={{ marginBottom:20 }}>
                <label style={Wlbl}>CREATE PASSWORD</label>
                <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} style={Winp} placeholder="Minimum 6 characters" onKeyDown={e=>e.key==="Enter"&&completePassword()} />
              </div>
              {errMsg && <p style={Werr}>{errMsg}</p>}
              <button className="wbtn" style={WbtnP} onClick={completePassword} disabled={loading}>{loading?"Creating account…":"Create account"}</button>
            </>}

          </div>{/* end form card */}

          {/* ── QR CARD — login + signup modes only ── */}
          {(mode === "login" || mode === "signup") && (
            <div style={{ flexShrink:0, width:252, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(20,184,166,0.25)", borderRadius:20, padding:"28px 22px", boxShadow:"0 24px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)", backdropFilter:"blur(12px)", display:"flex", flexDirection:"column", alignItems:"center", animation:"fadeUp .5s ease both" }}>

              {/* header */}
              <div style={{ display:"flex", alignItems:"center", gap:10, width:"100%", marginBottom:20, paddingBottom:16, borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ width:36, height:36, background:"linear-gradient(135deg,rgba(20,184,166,0.2),rgba(8,145,178,0.2))", border:"1px solid rgba(20,184,166,0.3)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                    <path d="M14 14h3v3M17 20h3M20 17v3"/>
                  </svg>
                </div>
                <div>
                  <p style={{ color:"#f1f5f9", fontWeight:700, fontSize:14, margin:0, letterSpacing:"-0.2px" }}>Sign in with QR</p>
                  <p style={{ color:"rgba(255,255,255,0.3)", fontSize:11, margin:"2px 0 0" }}>Scan with your phone</p>
                </div>
              </div>

              {/* QR frame */}
              <div style={{ width:186, height:186, background:"#fff", borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", marginBottom:20, padding:6, boxShadow:"0 0 0 1px rgba(20,184,166,0.15), 0 8px 24px rgba(0,0,0,0.3)" }}>
                {qrSt === "loading" && <div style={{ width:30, height:30, border:"3px solid #e2e8f0", borderTopColor:"#14b8a6", borderRadius:"50%", animation:"spin .8s linear infinite" }}/>}
                {qrSt === "error"   && <button className="wbtn" onClick={generateQR} style={{ padding:"8px 16px", background:"#14b8a6", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600 }}>Retry</button>}
                {qrSt === "ready"   && <img src={qrUrl} alt="QR" style={{ width:"100%", height:"100%", display:"block", borderRadius:8 }}/>}
                {qrSt === "done"    && (
                  <div style={{ textAlign:"center" }}>
                    <div style={{ width:56, height:56, borderRadius:"50%", background:"rgba(20,184,166,0.15)", border:"2px solid #14b8a6", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <p style={{ color:"#14b8a6", fontSize:12, fontWeight:700 }}>Authenticated!</p>
                  </div>
                )}
              </div>

              {/* steps */}
              <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
                {["Open your phone camera","Point at the QR code","Complete sign-in on phone"].map((s,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:20, height:20, borderRadius:"50%", background:"rgba(20,184,166,0.12)", border:"1px solid rgba(20,184,166,0.25)", color:"#14b8a6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, flexShrink:0 }}>{i+1}</div>
                    <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)", lineHeight:1.4 }}>{s}</span>
                  </div>
                ))}
              </div>

              {/* status footer */}
              {qrSt === "ready" && (
                <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.06)", width:"100%" }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:"#14b8a6", animation:"pulse 1.5s ease-in-out infinite", flexShrink:0, boxShadow:"0 0 6px #14b8a6" }}/>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)", flex:1 }}>Waiting for scan</span>
                  <button className="wbtn" onClick={generateQR} style={{ fontSize:11, color:"rgba(255,255,255,0.35)", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:6, cursor:"pointer", padding:"4px 10px", fontWeight:500 }}>Refresh</button>
                </div>
              )}
            </div>
          )}

        </div>{/* end body row */}
    </div>
  );
}

// ── TV style helpers ──────────────────────────────────────────────
const TVlbl  = fs => ({ display:"block", fontSize:12*fs, fontWeight:600, color:"#475569", marginBottom:8*fs, textTransform:"uppercase", letterSpacing:".5px" });
const TVinp  = fs => ({ width:"100%", padding:`${13*fs}px ${16*fs}px`, background:"#020b18", border:"1px solid #0f2035", borderRadius:10, color:"#f1f5f9", fontSize:15*fs, outline:"none" });
const TVbtnP = fs => ({ padding:`${14*fs}px`, background:"#14b8a6", color:"#fff", border:"none", borderRadius:10, fontSize:16*fs, fontWeight:700, cursor:"pointer", width:"100%", letterSpacing:".3px" });
const TVbtnO = fs => ({ padding:`${13*fs}px`, background:"transparent", color:"#94a3b8", border:"1px solid #1e3a5f", borderRadius:10, fontSize:15*fs, fontWeight:500, cursor:"pointer", width:"100%" });
const TVerr  = { color:"#f87171", fontSize:13, margin:0, background:"rgba(248,113,113,.1)", padding:"8px 12px", borderRadius:8 };

// ── Web style helpers ─────────────────────────────────────────────
const Wlbl = { display:"block", fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.3)", marginBottom:8, letterSpacing:"0.1em" };
const Winp = { width:"100%", padding:"13px 16px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, color:"#f1f5f9", fontSize:15, transition:"border-color .15s, box-shadow .15s", outline:"none" };
const WbtnP = { width:"100%", padding:"14px", background:"linear-gradient(135deg,#14b8a6,#0891b2)", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer", marginBottom:10, boxShadow:"0 6px 20px rgba(20,184,166,.35)", letterSpacing:"0.03em" };
const WbtnS = { width:"100%", padding:"13px", background:"rgba(255,255,255,0.05)", color:"rgba(255,255,255,0.6)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:10, fontSize:14, fontWeight:500, cursor:"pointer", marginBottom:10, letterSpacing:"0.01em" };
const Werr  = { color:"#f87171", fontSize:12, marginBottom:12, background:"rgba(248,113,113,0.08)", padding:"10px 14px", borderRadius:8, border:"1px solid rgba(248,113,113,0.2)", lineHeight:1.5 };